import React, { useEffect, useState } from "react";
import { useTheme } from "@mui/material/styles";
import { Box, Typography } from "@mui/material";
import {
    PieChart,
    Pie,
    Cell,
    Tooltip,
    ResponsiveContainer,
    PieLabelRenderProps,
} from "recharts";
import useAxios from "packages/novous_web/src/api/useAxios";
import { API_ENDPOINTS } from "packages/novous_web/src/api/endpoints";
import { useAuth } from '../../../../../../authContext/useAuth';

interface DriversByDrivingScoreChartProps {
    totalDrivers: number;
    title?: string;
    dateState?: any;
    driverId?: string;
}

const DriverCoachingScoreChart: React.FC<DriversByDrivingScoreChartProps> = ({
    totalDrivers,
    title = "Driving Incident Summary",
    dateState,
    driverId,
}) => {
    const { user } = useAuth();
    const { data: alertData, fetchData: fetchAlertData } = useAxios<any>(
        `${API_ENDPOINTS.alarm.DRIVER_ALERT_TREND_CHART}`,
        "POST"
    );
    const [driverAlertChartData, setDriverAlertChartData] = useState<any>();
    const [totalAlertValue, setTotalAlertValue] = useState<number>(0);
    const theme = useTheme();
    const COLORS = ["#F2994A", "#27AE60", "#F2C94C"];
    useEffect(() => {
      fetchAlertData({ fleetId: user?.customer_id, driverId: driverId, parentId: user?.parentId, startDate: dateState.startDate, endDate: dateState.endDate  })
    }, [dateState])
    useEffect(() => {
      if (alertData) {
        console.log('alertData', alertData);
        const driverAlertChartData = alertData?.data?.alertNameWithValueList?.map((alert: { alertName: any; alertValue: any; }, index: string | number) => ({
            warningName: alert.alertName,
            colorCode: alertData.data.coloursName[index] || "grey", // Use the color from coloursName or default to "grey"
            value: alert.alertValue,
        }));
        const totalValue = alertData?.data?.alertNameWithValueList?.reduce(
            (sum: number, alert: { alertValue: number }) => sum + alert.alertValue,
            0
        );
        setTotalAlertValue(totalValue);
        setDriverAlertChartData(driverAlertChartData);
      }
    }, [alertData])
    const renderCustomizedLabel = (props: PieLabelRenderProps) => {
        let { cx, cy } = props;
        if (cx === undefined || cy === undefined) {
            cx = 150;
            cy = 150;
        }
        return (
            <text
                x={cx}
                y={cy}
                textAnchor="middle"
                dominantBaseline="central"
                style={{ fill: "#000" }}
            >
                <tspan x={cx} dy="-0.5em" fontSize="16" fontWeight="bold">
                    {totalAlertValue}
                </tspan>
                <tspan x={cx} dy="1.2em" fontSize="12">
                    Alerts
                </tspan>
            </text>
        );
    };

    return (
        <Box className="w-full h-full flex flex-col border rounded-md p-4 gap-2 items-start mt-2">
            <Typography variant="body1" className="mb-4">
                {title}
            </Typography>
            <div className="flex sm:flex-row w-full justify-center items-center flex-col gap-4">
                <Box className="w-full flex-1 h-full flex justify-center items-center">
                    <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                            <Pie
                                data={driverAlertChartData}
                                dataKey="value"
                                nameKey="name"
                                innerRadius="60%"
                                outerRadius="90%"
                                labelLine={false}
                                label={renderCustomizedLabel}
                            >
                                {driverAlertChartData?.map((entry: { colorCode: string | undefined; }, index: any) => (
                                    <Cell key={`cell-${index}`} fill={entry.colorCode} />
                                ))}
                            </Pie>
                            <Tooltip
                                contentStyle={{
                                    background: theme.palette.background.paper,
                                    color: theme.palette.text.primary,
                                }}
                            />
                        </PieChart>
                    </ResponsiveContainer>
                </Box>
                <Box className=" flex flex-1 flex-col items-center flex-wrap">
                    <div>
                        {driverAlertChartData?.map((entry: any, index: any) => (
                            <Box key={entry.warningName} className="flex items-center mr-4">
                                <Box
                                    sx={{
                                        width: 12,
                                        height: 12,
                                        backgroundColor: entry.colorCode,
                                        borderRadius: "50%",
                                        marginRight: 1,
                                    }}
                                    className=""
                                />
                                <div className="flex gap-2">
                                    <Typography variant="body2" fontWeight={400}>{entry.warningName}</Typography>
                                    <Typography variant="body2" fontWeight={600}>{entry.value}</Typography>
                                    <Typography variant="body2" fontWeight={400}>{`(${((entry.value / totalAlertValue) * 100).toFixed(2)}%)`}</Typography>
                                </div>
                            </Box>
                        ))}
                    </div>
                </Box>
            </div>
        </Box>
    );
};

export default DriverCoachingScoreChart;
